import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { HiOutlineMagnifyingGlass, HiOutlineXMark, HiOutlineBuildingOffice2 } from 'react-icons/hi2';

import SearchAPI from '../../services/api/search.api';
import { ROUTES } from '../../helpers/constants';

// Lupa de la barra superior: busca colaboradores por RUT (con o sin puntos),
// nombre o correo y, para superadmin, empresas. Al elegir uno lleva a su ficha.

const MIN_LENGTH = 2;
const DEBOUNCE_MS = 250;

interface CollaboratorResult {
  id: string;
  name: string;
  surname: string;
  rut: string;
  email: string;
  position: string | null;
  area: string | null;
  clientId: string;
  clientName: string | null;
  testsCount: number;
}

interface ClientResult {
  id: string;
  name: string;
  rut: string;
  sector: string | null;
  collaboratorsCount: number;
}

type Item = { kind: 'collaborator'; data: CollaboratorResult } | { kind: 'client'; data: ClientResult };

const initials = (text = '') =>
  text.trim().split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?';

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export const GlobalSearch: React.FC<{ isSuperAdmin: boolean }> = ({ isSuperAdmin }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [results, setResults] = useState<{ collaborators: CollaboratorResult[]; clients: ClientResult[] }>({ collaborators: [], clients: [] });
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const requestRef = useRef(0);

  const items: Item[] = useMemo(() => [
    ...results.collaborators.map((data) => ({ kind: 'collaborator' as const, data })),
    ...results.clients.map((data) => ({ kind: 'client' as const, data })),
  ], [results]);

  const close = () => {
    setOpen(false);
    setQuery('');
    setResults({ collaborators: [], clients: [] });
    setFailed(false);
  };

  // Ctrl+K / Cmd+K abre la búsqueda desde cualquier pantalla del panel.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => { close(); }, [pathname]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    const text = query.trim();
    if (text.length < MIN_LENGTH) {
      requestRef.current += 1;
      setLoading(false);
      setResults({ collaborators: [], clients: [] });
      return undefined;
    }
    setLoading(true);
    const requestId = ++requestRef.current;
    const timer = setTimeout(async () => {
      try {
        const res = await SearchAPI.search(text);
        if (requestId !== requestRef.current) return;
        setResults({ collaborators: res?.data?.collaborators || [], clients: res?.data?.clients || [] });
        setFailed(false);
        setActive(0);
      } catch {
        if (requestId === requestRef.current) setFailed(true);
      } finally {
        if (requestId === requestRef.current) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const select = (item: Item) => {
    if (item.kind === 'client') {
      navigate(`${ROUTES.CLIENTS.route}/${item.data.id}`);
    } else {
      const c = item.data;
      navigate(`${ROUTES.CLIENTS.route}/collaborator/${c.id}`, {
        state: {
          collaborator: { id: c.id, name: c.name, surname: c.surname },
          client: { id: c.clientId, name: c.clientName },
        },
      });
    }
    close();
  };

  const onInputKey = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') close();
    if (!items.length) return;
    if (event.key === 'ArrowDown') { event.preventDefault(); setActive((i) => (i + 1) % items.length); }
    if (event.key === 'ArrowUp') { event.preventDefault(); setActive((i) => (i - 1 + items.length) % items.length); }
    if (event.key === 'Enter') { event.preventDefault(); select(items[active] || items[0]); }
  };

  const text = query.trim();
  const showEmpty = text.length >= MIN_LENGTH && !loading && !failed && !items.length;

  const renderItem = (item: Item, index: number) => {
    const isActive = index === active;
    const key = `${item.kind}-${item.data.id}`;
    const common = {
      role: 'option',
      'aria-selected': isActive,
      className: `gm-search-item${isActive ? ' is-active' : ''}`,
      onMouseEnter: () => setActive(index),
      onClick: () => select(item),
    };
    if (item.kind === 'client') {
      const c = item.data;
      return (
        <li key={key} {...common}>
          <span className="gm-avatar gm-avatar--square"><HiOutlineBuildingOffice2 size={16} /></span>
          <div className="gm-search-text">
            <b>{c.name}</b>
            <span>{[c.rut && `RUT ${c.rut}`, c.sector].filter(Boolean).join(' · ')}</span>
          </div>
          <span className="gm-search-aside">{plural(c.collaboratorsCount, 'colaborador', 'colaboradores')}</span>
        </li>
      );
    }
    const c = item.data;
    const name = `${c.name || ''} ${c.surname || ''}`.trim();
    return (
      <li key={key} {...common}>
        <span className="gm-avatar">{initials(name)}</span>
        <div className="gm-search-text">
          <b>{name || 'Sin nombre'}</b>
          <span>{[c.rut && `RUT ${c.rut}`, c.email].filter(Boolean).join(' · ')}</span>
          <span>{[isSuperAdmin && c.clientName, c.position || c.area].filter(Boolean).join(' · ')}</span>
        </div>
        <span className="gm-search-aside">{plural(c.testsCount, 'test', 'tests')}</span>
      </li>
    );
  };

  return (
    <>
      <button type="button" className="gm-search-trigger" onClick={() => setOpen(true)} aria-label="Buscar" title="Buscar (Ctrl+K)">
        <HiOutlineMagnifyingGlass size={18} />
        <span className="gm-search-trigger-label">Buscar</span>
        <kbd>Ctrl K</kbd>
      </button>
      {/* En document.body: el backdrop-filter de la barra superior encerraría el
          fondo oscuro dentro de la barra en vez de cubrir toda la pantalla. */}
      {open && createPortal(
        <div className="gm-search-overlay" onMouseDown={(event) => { if (event.target === event.currentTarget) close(); }}>
          <div className="gm-search-panel" role="dialog" aria-label="Búsqueda">
            <div className="gm-search-field">
              <HiOutlineMagnifyingGlass size={20} />
              <input
                ref={inputRef}
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={onInputKey}
                placeholder={isSuperAdmin ? 'RUT, nombre o correo de un colaborador, o una empresa' : 'RUT, nombre o correo de un colaborador'}
                aria-autocomplete="list"
                autoComplete="off"
              />
              <button type="button" className="gm-icon-btn" onClick={close} aria-label="Cerrar búsqueda">
                <HiOutlineXMark size={20} />
              </button>
            </div>
            <div className="gm-search-body">
              {text.length < MIN_LENGTH && (
                <p className="gm-search-hint">Escribe al menos {MIN_LENGTH} caracteres. El RUT puede ir con o sin puntos y guion.</p>
              )}
              {loading && !items.length && <p className="gm-search-hint">Buscando…</p>}
              {failed && <p className="gm-search-hint">No se pudo buscar. Inténtalo de nuevo.</p>}
              {showEmpty && <p className="gm-search-hint">Sin resultados para «{text}».</p>}
              {!!results.collaborators.length && (
                <>
                  <div className="gm-search-group">Colaboradores</div>
                  <ul role="listbox">{results.collaborators.map((data, i) => renderItem({ kind: 'collaborator', data }, i))}</ul>
                </>
              )}
              {!!results.clients.length && (
                <>
                  <div className="gm-search-group">Empresas</div>
                  <ul role="listbox">
                    {results.clients.map((data, i) => renderItem({ kind: 'client', data }, results.collaborators.length + i))}
                  </ul>
                </>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};
